const GENERIC_FONT_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
])

export const formatCssFontFamily = (fontFamily: string) => {
  if (GENERIC_FONT_FAMILIES.has(fontFamily)) return fontFamily
  return `"${fontFamily.replace(/"/g, '\\"')}"`
}

export const isFontFileUrl = (url: string) => /\.(woff2?|ttf|otf)(?:[?#].*)?$/i.test(url)

export const getFontFileFormat = (url: string) => {
  const extension = url.match(/\.(woff2?|ttf|otf)(?:[?#].*)?$/i)?.[1]?.toLowerCase()
  return extension === 'ttf' ? 'truetype' : extension || 'woff2'
}

export const buildFontFaceCss = (fontFamily: string, fontUrl: string) => `
@font-face {
  font-family: ${formatCssFontFamily(fontFamily)};
  src: url("${fontUrl.replace(/"/g, '%22')}") format("${getFontFileFormat(fontUrl)}");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`

export interface ThemeFont {
  fontFamily: string
  cdn: string
}

const ROBOTO_CDN = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap'

export const normalizeThemeFont = (font?: { fontFamily: string, cdn?: string } | null): ThemeFont => {
  if (!font || font.fontFamily !== 'sans-serif') {
    return { fontFamily: font?.fontFamily ?? '', cdn: font?.cdn ?? '' }
  }

  return { ...font, fontFamily: 'Roboto', cdn: ROBOTO_CDN }
}

/**
 * Apply the selected theme font to the current document.
 *
 * This is also used by the static preview iframe. The preview can receive a
 * new font after the document has already loaded, so replacing the stylesheet
 * alone is not enough: wait until the browser has loaded the font before the
 * preview is considered ready.
 */
export const syncThemeFontStylesheet = async (font: ThemeFont) => {
  font = normalizeThemeFont(font)
  const cdn = font?.cdn || ''
  const existingLink = document.querySelector<HTMLLinkElement>('link[data-webfaster-theme-font]')
  const existingStyle = document.querySelector<HTMLStyleElement>('style[data-webfaster-theme-font]')

  existingLink?.remove()
  existingStyle?.remove()

  if (!cdn) return

  if (isFontFileUrl(cdn)) {
    const style = document.createElement('style')
    style.dataset.webfasterThemeFont = 'true'
    style.textContent = buildFontFaceCss(font.fontFamily, cdn)
    document.head.appendChild(style)

    try {
      await document.fonts.load(`400 16px ${formatCssFontFamily(font.fontFamily)}`)
    } catch {
      // Keep the fallback font if the CDN font cannot be loaded.
    }
    return
  }

  const href = cdn.includes('fonts.googleapis.com') && !cdn.includes('display=')
    ? `${cdn}${cdn.includes('?') ? '&' : '?'}display=swap`
    : cdn
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.webfasterThemeFont = 'true'
  document.head.appendChild(link)

  await new Promise<void>((resolve) => {
    link.addEventListener('load', () => resolve(), { once: true })
    link.addEventListener('error', () => resolve(), { once: true })
  })
}
